import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';

import { environment } from '@env';

import {
  InventoryItem,
  InventoryItemFilters,
  ListInventoryItemsResponse,
  DemandReport,
  PanelAvailabilityList,
  PanelAvailabilityZone,
  PanelResolution,
  ResetZonePayload,
  SetZonePanelsPayload,
  StockReport,
  SupplyChainAnalyticsStatus,
  SupplyChainRegenerateResponse,
  SupplyChainReportKey,
} from '../../../interfaces/supply-chain.interface';
import { QueryBuilder } from '../query-builder';
import { SupplyChainApiService } from './supply-chain-api.service';

/** Cliente de la API de Supply Chain. */
@Injectable()
export class SupplyChainHttpService extends SupplyChainApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.supplyChainApiGateway}supply-chain`;
  private readonly availabilityUrl = `${environment.salesApiGateway}quotes/panel-availability`;
  private readonly analyticsUrl = `${environment.supplyChainApiGateway}supply-chain-analytics`;

  listItems(filters: InventoryItemFilters, page: number): Observable<ListInventoryItemsResponse> {
    return this.http.get<ListInventoryItemsResponse>(
      QueryBuilder.fromParams(`${this.baseUrl}/items`, { filters, page }),
    );
  }

  setItemActive(id: number, isActive: boolean): Observable<InventoryItem> {
    return this.http.patch<InventoryItem>(`${this.baseUrl}/items/${id}`, { is_active: isActive });
  }

  listPanelAvailability(): Observable<PanelAvailabilityList> {
    // shortcut: acepta también el arreglo que manda el backend anterior; quitarlo cuando el objeto esté en prod.
    return this.http
      .get<PanelAvailabilityList | PanelAvailabilityZone[]>(this.availabilityUrl)
      .pipe(
        map((res) =>
          Array.isArray(res) ? { zones: res, last_run_at: null, warehouse_stock: {} } : res,
        ),
      );
  }

  resolvePanels(stateCode: string, municipalityCode?: string | null): Observable<PanelResolution> {
    let params = new HttpParams().set('state_code', stateCode);
    if (municipalityCode) params = params.set('municipality_code', municipalityCode);
    return this.http.get<PanelResolution>(`${this.availabilityUrl}/resolve`, { params });
  }

  setZonePanels(payload: SetZonePanelsPayload): Observable<PanelAvailabilityZone> {
    return this.http.put<PanelAvailabilityZone>(this.availabilityUrl, payload);
  }

  resetZone(payload: ResetZonePayload): Observable<unknown> {
    return this.http.post(`${this.availabilityUrl}/reset`, payload);
  }

  analyticsStatus(): Observable<SupplyChainAnalyticsStatus> {
    return this.http.get<SupplyChainAnalyticsStatus>(this.analyticsUrl);
  }

  demandReport(between: string): Observable<DemandReport> {
    return this.http.get<DemandReport>(`${this.analyticsUrl}/panel-demand-by-zone`, {
      params: new HttpParams().set('filter[between]', between),
    });
  }

  stockReport(between: string): Observable<StockReport> {
    return this.http.get<StockReport>(`${this.analyticsUrl}/stock-by-warehouse`, {
      params: new HttpParams().set('filter[between]', between),
    });
  }

  regenerateReport(
    report: SupplyChainReportKey,
    between: string,
  ): Observable<SupplyChainRegenerateResponse> {
    return this.http.post<SupplyChainRegenerateResponse>(
      `${this.analyticsUrl}/${report}/regenerate`,
      { between },
    );
  }
}
